import { fasCommentQuestion } from 'shared/@generated/icons/fasCommentQuestion';
import { User } from 'shared/@generated/models';
import { AnyObject } from 'shared/@types';

import { View } from 'react-native';
import { useIsLoggedIn } from 'shared/state/user';
import { useUIVisibility } from 'shared/state/ui-visibility';

import { BREVO_CONFIG } from 'shared/library/brevo';
import { Button } from 'shared/components/button';
import { useState } from 'react';
import { Text } from 'shared/components/text';

function isBusinessHours(): boolean {
  const { startHour, endHour, timezone } = BREVO_CONFIG.businessHours;

  const currentHour = new Date().toLocaleString('sv-SE', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false,
  });

  const hour = parseInt(currentHour, 10);

  return hour >= startHour && hour < endHour;
}

type QueuedCall = [method: string, data?: any];

let scriptLoaded = false;
const queue: QueuedCall[] = [];

export const hasLiveChat = true;

function loadScript() {
  if (typeof window === 'undefined' || scriptLoaded) return;

  window.BrevoConversationsID = BREVO_CONFIG.id;
  window.BrevoConversationsSetup = BREVO_CONFIG.setup;

  const script = Object.assign(document.createElement('script'), {
    async: true,
    src: BREVO_CONFIG.scriptUrl,
    onload: () => {
      scriptLoaded = true;
      flushQueue();
    },
  });

  document.head?.appendChild(script);
}

function flushQueue() {
  if (typeof window.BrevoConversations !== 'function') return;

  while (queue.length) {
    window.BrevoConversations(...queue.shift()!);
  }
}

function call(method: string, data?: any) {
  if (scriptLoaded && typeof window.BrevoConversations === 'function') {
    window.BrevoConversations(method, data);
  } else {
    queue.push([method, data]);
  }
}

export const openChat = () => {
  loadScript();
  call('openChat', true);
};

export const setTranslations = (translations: AnyObject) => {
  call('setLocale', translations);
};

export const identifyUser = (user: User | null) => {
  if (!user) return;

  call('updateIntegrationData', {
    uuid: user.uuid,
    firstName: user.first_name,
    lastName: user.last_name,
  });
};

export const pageView = (url: string) => {
  call('pageView', {
    url,
    title: document?.title ?? '',
  });
};

export function BrevoChatLauncher() {
  const isLoggedIn = useIsLoggedIn();
  const { isLiveChatVisible } = useUIVisibility();
  const [isLoading, setIsLoading] = useState(false);
  const isOnline = isBusinessHours();

  const handlePress = () => {
    openChat();

    if (!scriptLoaded) {
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 3000);
    }
  };

  return (
    isLiveChatVisible && (
      <View
        className={`z-100 fixed right-0 m-3 md:m-5 ${isLoggedIn ? 'bottom-[calc(var(--tabs-h)+var(--safe-bottom))] md:bottom-0' : 'bottom-0'}`}
      >
        {isOnline && (
          <View
            className={
              'pointer-events-none absolute left-0 right-0 top-0 z-10 hidden -translate-y-1/2 items-center md:flex'
            }
          >
            <View
              className={'bg-brand-cream-light flex-row items-center gap-1 rounded-full px-2 py-1'}
            >
              <View className={'size-2 rounded-full bg-green-500'} />
              <Text className={'text-2xs text-brand-brown-dark'}>Online</Text>
            </View>
          </View>
        )}

        <Button
          className={'size-12 rounded-full !px-0 md:size-auto md:!px-4'}
          icon={fasCommentQuestion}
          iconPosition={'left'}
          text={undefined}
          accessibilityLabel={'Support'}
          color={'brown'}
          loading={isLoading}
          onPress={handlePress}
        >
          <Text className={'text-brand-cream ml-1.5 hidden font-bold md:flex'}>Support</Text>
        </Button>
      </View>
    )
  );
}
